import { DealType } from 'components/common/types/Deal.types';
import { getPattern } from 'components/pages/DealWithLines/common/Main/Line/Schedule/Pattern/daysInfo';
import { getDateInRequestFormat } from 'utils/dateUtil';
import { getVisualUnitData } from 'utils/framesUtils';
import { transformSelectedSegmentData } from 'utils/transformSelectedSegmentData';
import {
  getFilesOfCurrentLine,
  getFilesToUpload,
  getTargetData,
  getCodes,
  getCodesWithMediaOwner,
  getListData,
  transformLineServices,
} from 'components/pages/DealWithLines/common/transformPostData';

export const getMinimumPoiPointsData = (poiPoints) => {
  return poiPoints.map(({ id, name }) => ({
    id,
    name,
  }));
};

export const getPlannerPoiData = (plannerPoi) => {
  return plannerPoi.map(({ name, code, include, radius }) => ({
    categoryName: name,
    categoryType: code,
    include,
    radius,
  }));
};

export const getOpenStreetMapPoiData = (openStreetMapPoi) => {
  return openStreetMapPoi.map(({ name, include, radius }) => ({
    name,
    include,
    radius,
  }));
};

export const getProximity = (proximityData) => {
  const proximity = {
    openStreetMapPoi: null,
    plannerPoi: null,
  };

  if (proximityData.plannerPoi.length > 0) {
    proximity.plannerPoi = getPlannerPoiData(proximityData.plannerPoi);
  }

  if (proximityData.openStreetMapPoi.length > 0) {
    proximity.openStreetMapPoi = getOpenStreetMapPoiData(proximityData.openStreetMapPoi);
  }

  return {
    proximity: {
      ...proximity,
    },
  };
};

const getMediaOwners = (mediaOwners, mediaOwnerMeta) => {
  if (mediaOwners.length > 0) {
    return { mediaOwners };
  }

  if (mediaOwnerMeta?.length > 0) {
    return {
      mediaOwnerMeta: Object.fromEntries(mediaOwnerMeta.map(({ code, fee }) => [code, fee != null ? { fee } : {}])),
    };
  }

  return {};
};

/* eslint-disable sonarjs/cognitive-complexity */
export const getLineData = ({
  id,
  name,
  startDate,
  endDate,
  cpm,
  impressions,
  budget,
  sot,
  sweep,
  frames,
  countries,
  counties,
  cities,
  streets,
  postCodes,
  environments,
  productFormats,
  selectedDays,
  patternLength,
  tags,
  listFiles,
  routeFrameCodes,
  visualUnitCodes,
  visualUnitFiles,
  frameCpmFiles,
  proximity,
  venueTaxonomies,
  secondaryAudience,
  indexOptimisation,
  ceilingSot = null,
  floorSot = null,
  mediaOwners = [],
  mediaOwnerMeta = [],
  maxCPM = null,
  minCPM = null,
  creativeType = null,
  environmentId,
  distributionOfAvailabilityFilter,
  serviceActivations,
}) => {
  return {
    ...(id ? { internalId: id } : {}),
    ...getTargetData({ impressions, budget, sot, sweep, ceilingSot, floorSot }),
    ...(frames ? { frameCount: frames } : {}),
    ...(countries?.length ? { countries: getCodesWithMediaOwner(countries) } : {}),
    ...(counties?.length ? { counties: getCodesWithMediaOwner(counties) } : {}),
    ...(cities?.length ? { towns: getCodesWithMediaOwner(cities) } : {}),
    ...(streets?.length ? { address: getCodes(streets) } : {}),
    ...(postCodes?.length ? { postcodes: getCodesWithMediaOwner(postCodes, environmentId) } : {}),
    ...(environments.length ? { environmentCode: getCodes(environments) } : {}),
    ...(productFormats?.length ? { productFormatCode: getCodes(productFormats) } : {}),
    ...(Object.values(selectedDays).length
      ? { pattern: { patternLength, pattern: getPattern(selectedDays, startDate), patternType: 1 } }
      : {}),
    ...(tags.length ? { tags: tags.map(({ code: tagCode, include }) => ({ tagCode, include })) } : {}),
    ...getListData(routeFrameCodes, listFiles),
    ...getVisualUnitData(visualUnitCodes, visualUnitFiles),
    ...(name ? { dealName: name } : {}),
    startDate: getDateInRequestFormat(startDate, [0, 0, 0, 0]),
    endDate: getDateInRequestFormat(endDate, [23, 59, 59, 999]),
    ...(cpm ? { floorCPM: cpm } : {}),
    ...getProximity(proximity),
    ...(venueTaxonomies.length ? { venueTaxonomyEnumerationIds: venueTaxonomies } : {}),
    ...(indexOptimisation?.length ? { indexOptimisation } : {}),
    ...(secondaryAudience?.length ? { secondaryAudience } : {}),
    ...getMediaOwners(mediaOwners, mediaOwnerMeta),
    ...(maxCPM ? { maxCPM } : {}),
    ...(minCPM ? { minCPM } : {}),
    ...(frameCpmFiles?.length
      ? {
          preferredPricing: frameCpmFiles.map(({ name: fileName }) => ({
            fileName,
          })),
        }
      : {}),
    ...(creativeType ? { creativeType } : {}),
    serviceActivations,
    ...(distributionOfAvailabilityFilter
      ? { distributionOfAvailabilityFilter }
      : {
          distributionOfAvailabilityFilter: {
            distributionBy: 'frames',
            groupBy: 'town',
            selectedItems: [],
            locked: false,
          },
        }),
  };
};
/* eslint-enable sonarjs/cognitive-complexity */

export const getDealData = ({
  internalId,
  overriddenCampaignId,
  dealName,
  cpm,
  dealType: dealTypeCode,
  dealPriority,
  auctionModel,
  bookingStatusCode,
  advertiser,
  brand,
  productCategory,
  dsp,
  agency,
  specialist,
  salesPerson,
  salesTeam,
  externalReference,
  syncWithDsp,
  dspSeatId,
  enableCreativeSubmissionInBidStream,
  enableOpenMarketplace,
  hasAdsDealLevelCPMEnabled,
  overiddenCampaignId = null,
}) => {
  return {
    dealTypeCode,
    dealPriority,
    bookingStatusCode,
    enableCreativeSubmissionInBidStream,
    enableOpenMarketplace,
    advertiserCode: advertiser?.code,
    brandCode: brand?.code,
    productCategoryCode: productCategory?.code,
    ...(cpm && hasAdsDealLevelCPMEnabled ? { floorCPM: cpm } : {}),
    dspCode: dsp?.code,
    ...(internalId ? { internalId } : {}),
    ...(specialist?.code ? { specialistCode: specialist.code } : {}),
    ...(agency?.code ? { agencyCode: agency.code } : {}),
    ...(salesPerson ? { salesPersonName: salesPerson.name, salesPersonCode: salesPerson.code } : {}),
    ...(salesTeam ? { salesTeamName: salesTeam.name, salesTeamCode: salesTeam.code } : {}),
    ...(dealTypeCode === DealType.NON_GUARANTEED_FLOOR_PRICE ? { auctionModel } : {}),
    ...(dealName ? { name: dealName } : {}),
    ...(overriddenCampaignId ? { overriddenCampaignId } : {}),
    ...(externalReference ? { externalReference } : {}),
    ...(syncWithDsp && dspSeatId ? { dspSeatId, syncWithDsp } : {}),
    ...(overiddenCampaignId ? { overiddenCampaignId } : {}),
  };
};

export const getDealWithLineFormData = (formData, areServicesChanged) => {
  const lineFiles = getFilesOfCurrentLine(formData.currentLine);
  const frameCpmFiles = formData.currentLine.frameCpmFiles ?? [];

  const filesToUpload = getFilesToUpload([...lineFiles, ...frameCpmFiles]);

  const requestData = new FormData();

  Object.entries(filesToUpload).forEach(([fileName, file]) => {
    requestData.append(fileName, file);
  });

  const segment = transformSelectedSegmentData(formData.currentLine.segment);

  const serviceActivations = transformLineServices({
    services: formData.selectedNonMediaServices,
    isNewLine: true,
    areServicesChanged,
  });

  const deal = getDealData(formData);
  const dealLine = getLineData({
    ...formData.currentLine,
    cpm: deal.floorCPM ? null : formData.currentLine.cpm,
    secondaryAudience: segment ? [segment] : null,
    environmentId: formData.environmentId,
    serviceActivations,
  });

  requestData.append(
    'data',
    JSON.stringify({
      ...deal,
      dealLine,
    }),
  );

  return requestData;
};

export const getLineFormData = (formData, isCpmCampaignLevel, environmentId, lineId, areServicesChanged) => {
  const filesToUpload = getFilesToUpload(getFilesOfCurrentLine(formData.currentLine));

  const requestData = new FormData();

  Object.entries(filesToUpload).forEach(([fileName, file]) => {
    requestData.append(fileName, file);
  });

  const segment = transformSelectedSegmentData(formData.currentLine.segment);

  const serviceActivations = transformLineServices({
    services: formData.selectedNonMediaServices,
    isNewLine: !lineId,
    areServicesChanged,
  });

  requestData.append(
    'data',
    JSON.stringify({
      ...getLineData({
        ...formData.currentLine,
        cpm: isCpmCampaignLevel ? undefined : formData.currentLine.cpm,
        secondaryAudience: segment ? [segment] : null,
        environmentId,
        serviceActivations,
      }),
    }),
  );

  return requestData;
};
